import { useEffect, useState } from 'react';
import { copy } from '../../copy/es';
import { api, textoDeError } from '../../lib/api';
import { capitalizar, fechaHora, nombrePeriodo, numero, periodoCierre } from '../../lib/formato';
import { PeriodoSelect } from '../PeriodoSelect';
import { useSesion } from '../sesion';

interface Descarga {
  periodo: string;
  estado: 'pendiente' | 'generando' | 'lista' | 'fallida' | 'vencida';
  progreso: { centros_total?: number; archivos?: number };
  archivos: number | null;
  centros: number | null;
  tamanoBytes: number | null;
  solicitadaPor: string;
  solicitadaEn: string;
  terminadaEn: string | null;
  venceEn: string | null;
  error: string | null;
}

const tamano = (b: number | null) =>
  b === null
    ? '–'
    : b >= 1e9
      ? `${(b / 1e9).toFixed(1)} GB`
      : `${Math.max(1, Math.round(b / 1e6))} MB`;

/** Monthly download for operators (RF-22): one zip with every lot of a closing month. */
export function DescargasPage() {
  const { usuario } = useSesion();
  const c = copy.admin.descargas;
  const [periodo, setPeriodo] = useState(periodoCierre());
  const [filas, setFilas] = useState<Descarga[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const activa = filas.some((f) => f.estado === 'pendiente' || f.estado === 'generando');

  async function cargar() {
    try {
      setFilas(await api<Descarga[]>('/api/admin/descargas'));
    } catch (e) {
      setError(textoDeError(e));
    }
  }
  useEffect(() => {
    void cargar();
  }, []);
  useEffect(() => {
    if (!activa) return;
    const t = setInterval(() => void cargar(), 5000);
    return () => clearInterval(t);
  }, [activa]);

  async function preparar() {
    setError(null);
    setEnviando(true);
    try {
      await api(`/api/admin/descargas/${periodo}`, { method: 'POST' });
      await cargar();
    } catch (e) {
      setError(textoDeError(e));
    } finally {
      setEnviando(false);
    }
  }

  if (usuario?.rol !== 'operador') return <p className="sub">{c.soloOperador}</p>;
  const delMes = filas.find((f) => f.periodo === periodo);

  return (
    <div>
      <h2>{c.titulo}</h2>
      <p className="sub">{c.subtitulo}</p>
      <PeriodoSelect id="periodo-desc" etiqueta={c.periodo} valor={periodo} onChange={setPeriodo} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <button
          className="cta amber"
          disabled={enviando || delMes?.estado === 'pendiente' || delMes?.estado === 'generando'}
          onClick={() => void preparar()}
        >
          {delMes?.estado === 'lista' ? c.prepararDeNuevo : c.preparar}
        </button>
        {delMes?.estado === 'lista' && (
          <a className="ghost" href={`/api/admin/descargas/${periodo}/archivo`}>
            {c.descargar} · {tamano(delMes.tamanoBytes)}
          </a>
        )}
      </div>
      {error && (
        <div className="ok-box bad" role="alert">
          {error}
        </div>
      )}
      {delMes && (delMes.estado === 'pendiente' || delMes.estado === 'generando') && (
        <div className="ok-box" role="status">
          <b>{c.estados[delMes.estado]}</b>
          {delMes.progreso.archivos ? (
            <> · {c.contenido(delMes.progreso.archivos, delMes.progreso.centros_total ?? 0)}</>
          ) : null}
          <div style={{ marginTop: 6 }}>{c.nota}</div>
        </div>
      )}
      {delMes?.estado === 'fallida' && delMes.error && (
        <div className="ok-box bad" role="alert">
          {delMes.error}
        </div>
      )}

      {filas.length > 0 && (
        <>
          <div className="seccion-lbl">{c.historial.toUpperCase()}</div>
          <table className="mk">
            <thead>
              <tr>
                <th>{c.col.mes}</th>
                <th>{c.col.estado}</th>
                <th>{c.col.contenido}</th>
                <th>{c.col.tamano}</th>
                <th>{c.col.preparadoPor}</th>
                <th>{c.col.vence}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.periodo}>
                  <td>
                    <b>{capitalizar(nombrePeriodo(f.periodo))}</b>
                  </td>
                  <td>
                    <span
                      className={`pill ${f.estado === 'lista' ? 'p-ok' : f.estado === 'fallida' ? 'p-bad' : 'p-mid'}`}
                    >
                      {c.estados[f.estado]}
                    </span>
                  </td>
                  <td>{f.archivos ? c.contenido(f.archivos, f.centros ?? 0) : '–'}</td>
                  <td>{tamano(f.tamanoBytes)}</td>
                  <td>
                    {f.solicitadaPor}
                    <div style={{ fontSize: 12, color: '#8b8b8b' }}>
                      {fechaHora(f.solicitadaEn)}
                    </div>
                  </td>
                  <td>{f.estado === 'lista' ? fechaHora(f.venceEn) : '–'}</td>
                  <td>
                    {f.estado === 'lista' && (
                      <a href={`/api/admin/descargas/${f.periodo}/archivo`}>{c.descargar}</a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {filas.length > 0 && <div className="hist">{c.pie(numero(7))}</div>}
    </div>
  );
}
